import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../lib/contexts/AuthContext';
import { DynamicTitle } from '../DynamicTitle';
import { getRestaurantes } from '../../database/database';
import { propuestasService } from '../../lib/services/propuestas';
import { getImageDisplayUrl } from '../../lib/utils/imageTransform';
import {
  rankRestaurantesFichaPorDistancia,
  type RestauranteCercano,
} from '../../lib/utils/propuestaDistancia';
import { getCurrentUserPosition } from '../../lib/utils/userGeolocation';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';
import { fetchEdicionEscaparateComensalCached } from '../../lib/cache/comensalReadCache';

function formatDistanciaKmEs(km: number): string {
  if (km < 1) {
    const m = Math.round(km * 1000);
    return `${m} m`;
  }
  return `${km.toLocaleString('es-AR', { maximumFractionDigits: 1, minimumFractionDigits: km < 10 ? 1 : 0 })} km`;
}

type LocationState = { ciudadIds?: string[] } | null;

export function ComensalRestaurantesCercanosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading } = useAuth();
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCoordsReady, setMapCoordsReady] = useState(false);
  const [lista, setLista] = useState<RestauranteCercano[]>([]);
  const [loadingLista, setLoadingLista] = useState(false);

  const ciudadIds = useMemo(() => {
    const st = location.state as LocationState;
    if (st?.ciudadIds?.length) return st.ciudadIds.filter(Boolean);
    return profile?.ciudades_ids?.filter(Boolean) ?? [];
  }, [location.state, profile?.ciudades_ids]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setMapCoordsReady(false);
    getCurrentUserPosition()
      .then((p) => {
        if (!cancelled) setMapCoords({ lat: p.latitude, lng: p.longitude });
      })
      .catch(() => {
        if (!cancelled) setMapCoords(null);
      })
      .finally(() => {
        if (!cancelled) setMapCoordsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!mapCoords || ciudadIds.length === 0) {
      setLista([]);
      setLoadingLista(false);
      return;
    }
    let cancelled = false;
    setLoadingLista(true);
    getRestaurantes(ciudadIds)
      .then(async (list) => {
        if (cancelled) return;
        const ranked = rankRestaurantesFichaPorDistancia(list, mapCoords.lat, mapCoords.lng);
        const { id: edicionEscaparate } = await fetchEdicionEscaparateComensalCached();
        const linkMap = await propuestasService.mapPrimeraPropuestaPublicaPorRestauranteIds(
          ranked.map((x) => x.restaurante.id),
          edicionEscaparate
        );
        if (cancelled) return;
        const enriched: RestauranteCercano[] = ranked.map(({ restaurante, distanceKm }) => {
          const link = linkMap.get(restaurante.id);
          return {
            restaurante,
            distanceKm,
            propuestaEjemploId: link?.propuestaId,
            edicionEjemploId: link?.edicionId,
          };
        });
        setLista(enriched);
      })
      .catch(() => {
        if (!cancelled) setLista([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLista(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mapCoords, ciudadIds]);

  if (loading || !user || !profile) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#030213" />
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <DynamicTitle title="Restaurantes cercanos — As Gastronómico" />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backRow}
          onPress={() => navigate('/')}
          hitSlop={12}
          accessibilityRole="button"
          accessibilityLabel="Volver"
        >
          <Ionicons name="chevron-back" size={22} color="#030213" />
          <Text style={styles.backText}>Volver</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Restaurantes cercanos</Text>
        <Text style={styles.subtitle}>
          Ordenados por distancia respecto a tu ubicación en{' '}
          {ciudadIds.length > 1 ? 'las ciudades de tu perfil' : 'tu ciudad'}.
        </Text>
      </View>

      {ciudadIds.length === 0 ? (
        <Text style={[styles.hint, styles.pad]}>
          No hay ciudad seleccionada. Elige una en el inicio para ver restaurantes en tu zona.
        </Text>
      ) : !mapCoordsReady ? (
        <View style={styles.pad}>
          <ActivityIndicator size="small" color="#030213" />
          <Text style={styles.hint}>Obteniendo tu ubicación…</Text>
        </View>
      ) : !mapCoords ? (
        <Text style={[styles.hint, styles.pad288]}>
          Activa el permiso de ubicación para ver restaurantes ordenados por distancia.
        </Text>
      ) : loadingLista ? (
        <View style={styles.pad}>
          <ActivityIndicator size="small" color="#030213" />
        </View>
      ) : lista.length === 0 ? (
        <Text style={[styles.hint, styles.pad288]}>
          No hay restaurantes con ubicación en las ciudades seleccionadas.
        </Text>
      ) : (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {lista.map((item) => {
            const img = item.restaurante.logo?.trim() || null;
            const uri = img
              ? getImageDisplayUrl(img, { preset: 'card' }) || img.replace(/^http:/, 'https:')
              : null;
            const nombre =
              item.restaurante.nombre_mostrar?.trim() ||
              item.restaurante.nombre_oficial?.trim() ||
              '';
            const openFicha = () => {
              if (item.propuestaEjemploId && item.edicionEjemploId != null) {
                navigate(
                  `/edicion/${item.edicionEjemploId}#propuesta-${item.propuestaEjemploId}`
                );
              }
            };
            return (
              <TouchableOpacity
                key={item.restaurante.id}
                style={styles.card}
                activeOpacity={0.88}
                onPress={openFicha}
                disabled={!item.propuestaEjemploId || item.edicionEjemploId == null}
                accessibilityRole="button"
                accessibilityLabel={`${nombre}, a ${formatDistanciaKmEs(item.distanceKm)}`}
              >
                {uri ? (
                  <Image source={{ uri }} style={styles.cardImage} resizeMode="cover" />
                ) : (
                  <View style={[styles.cardImage, styles.cardImagePh]}>
                    <Ionicons name="restaurant-outline" size={36} color="#9ca3af" />
                  </View>
                )}
                <View style={styles.cardBody}>
                  <Text style={styles.cardTitle} numberOfLines={2}>
                    {nombre}
                  </Text>
                  {item.restaurante.ciudad_nombre ? (
                    <Text style={styles.cardMeta} numberOfLines={1}>
                      {item.restaurante.ciudad_nombre}
                    </Text>
                  ) : null}
                  <Text style={styles.cardDist}>{formatDistanciaKmEs(item.distanceKm)}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    marginBottom: 12,
  },
  backText: {
    fontSize: 16,
    color: '#030213',
    fontWeight: '600',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#030213',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  pad: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  pad288: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  hint: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 24 + COMENSAL_TAB_BAR_OVERLAY_INSET,
    gap: 12,
  },
  card: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
    minHeight: 100,
    shadowColor: '#030213',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  cardImage: {
    width: 108,
    alignSelf: 'stretch',
    backgroundColor: '#f3f4f6',
  },
  cardImagePh: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardBody: {
    flex: 1,
    padding: 12,
    justifyContent: 'center',
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#030213',
    lineHeight: 21,
  },
  cardMeta: {
    fontSize: 13,
    color: '#6b7280',
  },
  cardDist: {
    fontSize: 14,
    fontWeight: '600',
    color: '#030213',
    marginTop: 4,
  },
});
